import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import CommercialNotice from "../components/CommercialNotice";
import { Plus, ExternalLink, AlertCircle, CheckCircle2 } from "lucide-react";

const PRIORITY_COLORS = {
  high: "border-volt text-volt",
  medium: "border-line text-white",
  low: "border-line text-ink-muted",
};
const EMPTY = { brand_name: "", category: "", website: "", contact_email: "", contact_name: "", instagram_handle: "", reason_for_fit: "", priority: "medium" };

// Accepts an optional `embedded` prop — when true, the page omits its
// `<DashboardLayout>` wrapper so it can be nested inside BrandDirectory's
// tabbed parent (Feb 2026 nav redesign).
export default function Brands({ embedded = false }) {
  const [brands, setBrands] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [limitErr, setLimitErr] = useState("");
  const [saveErr, setSaveErr] = useState("");

  const load = () => api.get("/brands").then(({data}) => setBrands(data));
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const clearEmailInvalid = async (brandId) => {
    try { await api.post(`/brands/${brandId}/clear-email-invalid`); load(); }
    catch (e) { /* ignore — UI shows current state */ }
  };
  const save = async () => {
    setLimitErr(""); setSaveErr("");
    try {
      await api.post("/brands", form);
      setForm(EMPTY); setAdding(false); load();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (e.response?.status === 402) setLimitErr(msg); else setSaveErr(msg);
    }
  };

  const Wrapper = embedded ? Fragment : DashboardLayout;
  return (
    <Wrapper>
      <div className="p-6 md:p-12">
        <div className="flex justify-between items-end mb-10 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow">Opportunity database</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="brands-title">Brands</h1>
            <p className="text-ink-muted mt-2 text-sm">Your shortlist of dream sponsors. Each new brand creates a Target deal automatically.</p>
          </div>
          <button onClick={()=>setAdding(true)} className="btn-volt inline-flex items-center gap-2" data-testid="brands-add-btn">
            <Plus className="w-4 h-4" /> Add brand
          </button>
        </div>

        {limitErr && (
          <div className="surface p-5 mb-6 border-volt/40 flex items-start gap-3" data-testid="brands-limit">
            <AlertCircle className="w-5 h-5 text-volt mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-heading text-base uppercase tracking-tight">Plan limit reached</div>
              <div className="text-sm text-ink-muted mt-1">{limitErr}</div>
              <Link to="/app/pricing" className="btn-volt inline-block mt-3" data-testid="brands-upgrade-link">Upgrade plan</Link>
            </div>
          </div>
        )}

        {adding && (
          <div className="surface p-6 mb-6" data-testid="brands-add-form">
            <div className="grid md:grid-cols-2 gap-4">
              <F label="Brand name *"><I value={form.brand_name} onChange={(v)=>upd("brand_name", v)} testid="ab-name" /></F>
              <F label="Category"><I value={form.category} onChange={(v)=>upd("category", v)} testid="ab-cat" /></F>
              <F label="Website"><I value={form.website} onChange={(v)=>upd("website", v)} testid="ab-site" /></F>
              <F label="Contact name"><I value={form.contact_name} onChange={(v)=>upd("contact_name", v)} testid="ab-cname" /></F>
              <F label="Contact email"><I value={form.contact_email} onChange={(v)=>upd("contact_email", v)} testid="ab-email" /></F>
              <F label="Instagram handle"><I value={form.instagram_handle} onChange={(v)=>upd("instagram_handle", v)} testid="ab-ig" /></F>
              <F label="Priority">
                <select value={form.priority} onChange={(e)=>upd("priority", e.target.value)} data-testid="ab-prio" className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white">
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </F>
              <F label="Reason for fit" wide>
                <textarea rows={3} value={form.reason_for_fit} onChange={(e)=>upd("reason_for_fit", e.target.value)} data-testid="ab-fit"
                  className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white resize-none" />
              </F>
            </div>
            <div className="flex justify-end gap-2 mt-5 items-center">
              {saveErr && <div className="text-xs text-[#FF3B30] mr-auto" data-testid="brands-save-err">{saveErr}</div>}
              <button onClick={()=>{setAdding(false); setSaveErr("");}} className="btn-ghost-line">Cancel</button>
              <button onClick={save} disabled={!form.brand_name} className="btn-volt disabled:opacity-40" data-testid="ab-save">Save brand</button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="brands-grid">
          {brands.length === 0 ? (
            <div className="surface p-12 md:col-span-3 text-center">
              <div className="font-heading text-2xl uppercase">No brands yet</div>
              <p className="text-ink-muted text-sm mt-2">Add your first dream sponsor.</p>
            </div>
          ) : brands.map(b => (
            <div key={b.id} className="surface p-5" data-testid={`brand-${b.id}`}>
              <div className="flex justify-between items-start gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-xl uppercase break-words leading-tight">{b.brand_name}</div>
                  <div className="text-xs text-ink-muted mt-1 break-words">{b.category}</div>
                </div>
                <span className={`shrink-0 px-2 py-1 text-[10px] uppercase tracking-widest border ${PRIORITY_COLORS[b.priority] || PRIORITY_COLORS.medium}`}>{b.priority}</span>
              </div>
              {b.email_invalid && (
                <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid={`brand-${b.id}-invalid`}>
                  <div className="px-2 py-1.5 border border-[#FF3B30]/40 text-[10px] uppercase tracking-widest text-[#FF3B30] inline-flex items-center gap-1.5">
                    <AlertCircle className="w-3 h-3" />
                    Email invalid · {b.email_invalid_reason || "bounced"}
                  </div>
                  <button
                    onClick={() => clearEmailInvalid(b.id)}
                    className="px-2 py-1.5 border border-volt/50 text-[10px] uppercase tracking-widest text-volt hover:bg-volt hover:text-bg transition inline-flex items-center gap-1.5"
                    data-testid={`brand-${b.id}-mark-valid`}
                    title="Mark this email as valid again after updating the contact"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Email valid again
                  </button>
                </div>
              )}
              {b.reason_for_fit && <p className="text-sm text-ink-muted leading-relaxed">{b.reason_for_fit}</p>}
              <div className="mt-4 flex flex-col gap-1 text-xs text-ink-muted break-words">
                {b.contact_name && <div>{b.contact_name}</div>}
                {b.contact_email && <div className="break-all">{b.contact_email}</div>}
                {b.instagram_handle && <div className="break-all">{b.instagram_handle}</div>}
                {b.website && <a href={b.website.startsWith("http") ? b.website : `https://${b.website}`} target="_blank" rel="noreferrer" className="text-volt inline-flex items-center gap-1">Visit <ExternalLink className="w-3 h-3" /></a>}
              </div>
              <div className="mt-4 pt-4 border-t border-line flex justify-end">
                <Link to="/app/crm" className="text-xs uppercase tracking-widest text-volt">Open in CRM</Link>
              </div>
            </div>
          ))}
        </div>

        <CommercialNotice />
      </div>
    </Wrapper>
  );
}
function F({ label, children, wide }) { return <div className={wide ? "md:col-span-2" : ""}><label className="label-eyebrow block mb-2">{label}</label>{children}</div>; }
function I({ value, onChange, testid }) { return <input value={value} onChange={(e)=>onChange(e.target.value)} data-testid={testid} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white" />; }
